"use client";

import {
  useEnrollmentRounds,
  type EnrollmentBannerData,
} from "@/themes/default/templates/section/enrollment/useEnrollmentRounds";

// Same "+" pattern as the epona People spotlight band.
const CROSS_PATTERN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='44' height='44'%3E%3Cpath d='M22 14v16 M14 22h16' stroke='%23ffffff' stroke-width='1.1' stroke-linecap='round' opacity='0.16'/%3E%3C/svg%3E\")";

/**
 * epona enrollment banner — brand gradient band with the cross pattern,
 * Oswald uppercase heading, orange quick-button CTA + white outline button
 * (legacy Epona V2 idiom: small radius, flat shadow).
 */
export function EnrollmentBanner({
  section,
  currentLanguage = "mm",
}: {
  section: any;
  currentLanguage?: "en" | "mm";
}) {
  const d = useEnrollmentRounds(section, currentLanguage);
  if (!d) return null;
  if (section?.variant === "slide-card") return <EnrollmentSlideCard d={d} />;

  return (
    <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" lang={d.lang === "mm" ? "my" : "en"}>
      <div className="epona-shadow relative overflow-hidden rounded bg-epona-gradient text-white">
        <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: CROSS_PATTERN, backgroundSize: "44px 44px" }} aria-hidden="true" />
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(60% 90% at 82% 10%, rgba(255,255,255,0.16), transparent 60%)" }}
          aria-hidden="true"
        />
        {d.rounds.map((r, i) => (
          <div
            key={r.id}
            className={`relative flex flex-col gap-5 px-6 py-6 md:flex-row md:items-center md:px-9 md:py-7 ${
              i > 0 ? "border-t border-white/15" : ""
            }`}
          >
            <div className="min-w-0 flex-1">
              <p className="text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-white/80">{d.title}</p>
              <h2
                className="epona-heading mt-1.5 text-xl uppercase tracking-wide md:text-2xl"
                style={{ lineHeight: 1.5 }}
              >
                {r.label}
              </h2>
              <span className="mt-2 block h-[3px] w-14" style={{ backgroundColor: "var(--color-accent)" }} aria-hidden="true" />
              {r.window && (
                <p className="mt-2 text-sm text-white/85">
                  {d.t.windowPrefix} {r.window}
                </p>
              )}
            </div>
            <div className="flex shrink-0 flex-wrap gap-3">
              {r.listHref && (
                <a
                  href={r.listHref}
                  className="inline-block rounded px-5 py-2.5 text-sm font-semibold uppercase tracking-wide transition-opacity hover:opacity-90"
                  style={{ color: "#fff", border: "2px solid rgba(255,255,255,0.9)" }}
                >
                  {d.t.list}
                </a>
              )}
              <a
                href={r.enrollHref}
                className="epona-quick-btn inline-flex items-center gap-2 !px-6 !py-3 text-sm font-semibold uppercase tracking-wide"
              >
                {d.t.enroll} <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const SLIDE_CARD_TEXT = {
  mm: { open: "ဖွင့်ထားသည်", roster: "ကျောင်းသားစာရင်း", window: "လက်ခံမည့်ကာလ —" },
  en: { open: "Open now", roster: "Class list", window: "Enrollment:" },
};

/**
 * `variant: 'slide-card'` — the same rounds as a compact white card meant to
 * sit as a `section` layer on the home carousel (anchor bottom-right, layer
 * width 100%): the card hugs the right edge up to 380px and fills the layer
 * on phones. White surface so it reads over any slide photo.
 */
function EnrollmentSlideCard({ d }: { d: EnrollmentBannerData }) {
  const s = SLIDE_CARD_TEXT[d.lang];
  const first = d.rounds[0];
  return (
    <div className="flex justify-end" lang={d.lang === "mm" ? "my" : "en"}>
      {/* Phones: a one-line bar — the full card would cover the hero CTAs. */}
      <a
        href={first.enrollHref}
        className="flex w-full items-center gap-3 rounded-full bg-white py-1.5 pl-4 pr-1.5 text-[#1C2B39] sm:hidden"
        style={{ boxShadow: "0 12px 30px rgba(0,20,40,.45)" }}
      >
        <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#1fb36a] opacity-60 motion-reduce:animate-none" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-[#1fb36a]" />
        </span>
        <span className="line-clamp-2 min-w-0 flex-1 text-[0.8rem] font-semibold" style={{ lineHeight: 1.45 }}>
          {first.label}
        </span>
        <span className="epona-quick-btn shrink-0 whitespace-nowrap !rounded-full !px-3.5 !py-2 text-[0.8rem] font-semibold">
          {d.t.enroll} →
        </span>
      </a>
      <div
        className="relative hidden w-full max-w-[380px] overflow-hidden rounded-md bg-white text-[#1C2B39] sm:block"
        style={{ boxShadow: "0 22px 54px rgba(0,20,40,.42), 0 2px 6px rgba(0,20,40,.18)" }}
      >
        <span className="absolute inset-x-0 top-0 h-1 bg-epona-gradient" aria-hidden="true" />
        <span className="absolute left-6 top-1 h-[3px] w-14" style={{ backgroundColor: "var(--color-accent)" }} aria-hidden="true" />
        {d.rounds.map((r, i) => (
          <div key={r.id} className={`grid gap-3 px-5 pb-4 pt-6 sm:px-6 ${i > 0 ? "border-t border-[#e3eaf0]" : ""}`}>
            <div className="flex items-center justify-between gap-3 text-[0.7rem] font-semibold uppercase tracking-[0.14em]">
              <span style={{ color: "var(--color-primary)" }}>{d.title}</span>
              {i === 0 && (
                <span className="inline-flex shrink-0 items-center gap-1.5 normal-case tracking-[0.04em] text-[#17864f]">
                  <span className="relative flex h-2 w-2" aria-hidden="true">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#1fb36a] opacity-60 motion-reduce:animate-none" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-[#1fb36a]" />
                  </span>
                  {s.open}
                </span>
              )}
            </div>
            <h2 className="epona-heading m-0 text-lg tracking-wide md:text-[1.35rem]" style={{ lineHeight: 1.5, color: "#1C2B39" }}>
              {r.label}
            </h2>
            <div className="flex flex-wrap gap-2.5">
              <a
                href={r.enrollHref}
                className="epona-quick-btn inline-flex items-center gap-2 !px-5 !py-2.5 text-sm font-semibold"
                style={{ boxShadow: "0 6px 18px rgba(250,141,42,.35)" }}
              >
                {d.t.enroll} <span aria-hidden="true">→</span>
              </a>
              {r.listHref && (
                <a
                  href={r.listHref}
                  className="inline-flex items-center rounded-[5px] px-4 py-2 text-sm font-medium transition-colors hover:bg-[#EDF2FA]"
                  style={{ color: "var(--color-primary)", border: "1.5px solid rgba(0,67,123,.45)" }}
                >
                  {r.kind === "roster" ? s.roster : d.t.list}
                </a>
              )}
            </div>
            {r.window && (
              <p className="m-0 flex items-center gap-2 border-t border-[#e3eaf0] pt-2.5 text-[0.8rem] text-[#4d5f70]">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1598CB" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="16" rx="2" />
                  <path d="M3 10h18M8 3v4M16 3v4" />
                </svg>
                <span>{s.window} {r.window}</span>
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
